/*
 * http://dev.plm.d4r.int/sample-service/swagger-ui/index.html#/
 */
import { gatewayServices } from "src/common/apiUrl";
import { rest } from "src/common/rest";
import { ZUid } from "src/types/zUid";
import { zSampleSettings, ZSampleSettings } from "./SampleTypes";
import {
  zSampleColorsResponse,
  zSampleRecord,
  ZSampleRecord,
  zSampleRequest,
  ZSampleRequest,
  zSampleSuppliersResponse,
} from "./SampleControl/ZSampleRequest";
import { ZSampleData, zSampleData } from "./SampleControl/ZSampleData";
import { NewSampleData } from "./SampleControl/SamplePoints/SamplePointsTypes";

export const apiSample = (path: string) =>
  gatewayServices(`/sample-service/api${path}`);

// ----- settings

export const loadSampleSettings = async (
  objectId: ZUid,
): Promise<ZSampleSettings> => {
  const resp = await rest.get(apiSample("/settings"), { params: { objectId } });
  return zSampleSettings.parse(resp.data);
};

export const saveSampleSettings = async (
  objectId: ZUid,
  objectServiceId: ZUid,
  version: number,
  settings: ZSampleSettings,
) => {
  await rest.post(apiSample("/settings"), settings, {
    params: { objectId, serviceId: objectServiceId, version },
  });
};

// ------- summary

export const loadSampleSummary = async (
  modelEntityId: ZUid,
  modelObjectId: ZUid,
): Promise<ZSampleRequest[]> => {
  const resp = await rest.get(apiSample("/sample-request"), {
    params: { modelEntityId, modelObjectId },
  });
  return zSampleRequest.array().parse(resp.data);
};

export const createSampleRequest = async (
  modelObjectId: ZUid,
  requestData: Omit<ZSampleRequest, "id">,
) => {
  await rest.post(apiSample("/sample-request"), requestData, {
    params: { modelObjectId },
  });
};

export const updateSampleRequest = async (
  modelObjectId: ZUid,
  requestData: ZSampleRequest,
) => {
  await rest.put(apiSample("/sample-request"), requestData, {
    params: { modelObjectId },
  });
};

export const deleteSampleRequest = async (modelEntityId: ZUid, ids: ZUid[]) => {
  await rest.delete(apiSample("/sample-request"), {
    params: { modelEntityId },
    data: ids,
  });
};

export const loadSampleSuppliers = async (params: {
  modelObjectId: ZUid;
  page: number;
  size: number;
  query: string | undefined;
}) => {
  const resp = await rest.get(apiSample("/supplier"), { params });
  return zSampleSuppliersResponse.parse(resp.data);
};

export const loadSampleColors = async (params: {
  modelObjectId: ZUid;
  modelId: ZUid;
  page: number;
  size: number;
  query: string | undefined;
}) => {
  const resp = await rest.get(apiSample("/color"), { params });
  return zSampleColorsResponse.parse(resp.data);
};

export enum SampleDictionaryType {
  sampleRequestType = "SAMPLE_REQUEST_TYPE",
  fitStatus = "FIT_STATUS",
  requestType = "REQUEST_TYPE",
  requestStatus = "REQUEST_STATUS",
}

export const loadSampleDictionary = async (
  modelObjectId: ZUid,
  name: SampleDictionaryType,
): Promise<ZSampleRecord[]> => {
  const resp = await rest.get(apiSample("/dictionary"), {
    params: { modelObjectId, name },
  });
  return zSampleRecord.array().parse(resp.data);
};

// -------- points

export const loadSampleData = async (
  sampleRequestId: ZUid,
): Promise<ZSampleData[]> => {
  const resp = await rest.get(apiSample("/sample"), {
    params: { sampleRequestId },
  });
  return zSampleData.array().parse(resp.data);
};

export const createSampleData = async (
  sampleRequestId: ZUid,
  data: NewSampleData,
) => {
  const { sizeId, versionId } = data;
  await rest.post(apiSample("/sample"), undefined, {
    params: { sampleRequestId, sizeId, versionId },
  });
};

export const saveSampleData = async (
  params: {
    sampleRequestId: ZUid;
    sampleId: ZUid;
    target: string;
  },
  row: ZSampleData,
) => {
  await rest.put(apiSample("/sample"), row, { params });
};

export const deleteSampleData = async (sampleRequestId: ZUid, ids: ZUid[]) => {
  await rest.delete(apiSample("/sample"), {
    params: { sampleRequestId },
    data: ids,
  });
};
